import { useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, Pause, Play, RotateCcw, X } from 'lucide-react'
import { toast } from 'sonner'

import AASMActionControls from 'components/reusable/AASMActionControls'
import {
  getGetBrokersOperationsBatchesBatchIdQueryKey,
  usePostBrokersOperationsBatchesBatchId,
} from 'utils/web_api'
import {
  type BrokersOperationsBatch,
  type BrokersOperationsBatchAvailableActionsItem,
  type PostBrokersOperationsBatchesBatchIdAction,
} from 'utils/web_api.schemas'

type OperationsBatchActionControlsProps = {
  batch: BrokersOperationsBatch
  onSuccess?: (updatedBatch: BrokersOperationsBatch) => void
}

// Sort actions by priority (most common actions first)
const sortAvailableActions = (
  actions: BrokersOperationsBatchAvailableActionsItem[]
): BrokersOperationsBatchAvailableActionsItem[] => {
  const actionPriority: Record<BrokersOperationsBatchAvailableActionsItem, number> = {
    gather_candidates: 1,
    prepare: 2,
    execute_tasks: 3,
    confirm_and_complete: 4,
    pause: 5,
    retry_execution: 6,
    retry_preparation: 7,
    retry_from_start: 8,
    mark_failed: 9,
    cancel_batch: 10,
  }

  return [...actions].sort((a, b) => {
    const priorityA = actionPriority[a] ?? 999
    const priorityB = actionPriority[b] ?? 999
    return priorityA - priorityB
  })
}

const OperationsBatchActionControls: React.FC<OperationsBatchActionControlsProps> = ({
  batch,
  onSuccess,
}) => {
  const queryClient = useQueryClient()
  const mutation = usePostBrokersOperationsBatchesBatchId()

  const getActionLabel = (action: BrokersOperationsBatchAvailableActionsItem) => {
    switch (action) {
      case 'gather_candidates':
        return 'Gather Candidates'
      case 'prepare':
        return 'Prepare'
      case 'execute_tasks':
        return 'Execute Tasks'
      case 'confirm_and_complete':
        return 'Confirm & Complete'
      case 'retry_execution':
        return 'Retry Execution'
      case 'retry_preparation':
        return 'Retry Preparation'
      case 'retry_from_start':
        return 'Retry From Start'
      case 'mark_failed':
        return 'Mark Failed'
      case 'cancel_batch':
        return 'Cancel Batch'
      case 'pause':
        return 'Pause'
    }
  }

  const getActionIcon = (
    action: BrokersOperationsBatchAvailableActionsItem | undefined,
    isPending: boolean
  ) => {
    if (isPending) return <Loader2 className='w-4 h-4 animate-spin' />

    switch (action) {
      case 'gather_candidates':
      case 'execute_tasks':
      case 'retry_execution':
      case 'retry_preparation':
      case 'retry_from_start':
        return <Play className='w-4 h-4' />
      case 'pause':
        return <Pause className='w-4 h-4' />
      case 'cancel_batch':
      case 'mark_failed':
        return <X className='w-4 h-4' />
      case 'confirm_and_complete':
        return <Check className='w-4 h-4' />
      case 'prepare':
        return <RotateCcw className='w-4 h-4' />
      default:
        return <Play className='w-4 h-4' />
    }
  }

  const handleExecuteAction = (action: BrokersOperationsBatchAvailableActionsItem) => {
    mutation.mutate(
      {
        batchId: batch.id,
        data: {
          action: action as PostBrokersOperationsBatchesBatchIdAction,
        },
      },
      {
        onSuccess: (updatedBatch: BrokersOperationsBatch) => {
          // Update the batch cache directly with returned data
          queryClient.setQueryData(
            getGetBrokersOperationsBatchesBatchIdQueryKey(batch.id),
            updatedBatch
          )

          // Invalidate batch list queries
          queryClient.invalidateQueries({
            queryKey: ['brokers', 'operations_batches'],
          })

          // Call the optional onSuccess callback
          onSuccess?.(updatedBatch)
        },
        onError: (error: unknown) => {
          console.error('Failed to execute batch action:', error)
          toast.error('Failed to execute action. Please try again.')
        },
      }
    )
  }

  return (
    <AASMActionControls<BrokersOperationsBatchAvailableActionsItem>
      availableActions={batch.available_actions}
      sortActions={sortAvailableActions}
      getActionLabel={getActionLabel}
      getActionIcon={getActionIcon}
      onActionExecute={handleExecuteAction}
      isPending={mutation.isPending}
    />
  )
}

export default OperationsBatchActionControls
